/**
 * CAMBIO FINITURA ISTANTANEO (fork, tutti gli schermi: computer, telefono, totem) — richiesta di Davide del 7 ott 2026
 * sera ("Cambio finitura senza calcolo, per ora fai solo questo").
 *
 * La finitura (argento / oro giallo / oro rosa) non cambia la forma del gioiello: cambia solo il colore del metallo.
 * Fino a oggi era trattata come tutte le altre scelte: un calcolo pieno sul server ShapeDiver (3-4 s con una
 * combinazione nuova) prima di vedere il colore. Ora, nell'istante in cui il cliente sceglie la finitura, il fork
 * ricolora SUBITO nel visore le uscite di metallo ("Gioiello" = piastrina + anellini/gambo, "Catena"); il calcolo
 * di ShapeDiver parte comunque come prima (serve per il prezzo, per la scheda e per la configurazione da mettere nel
 * carrello) e quando arriva porta la geometria gia' del colore giusto: il nostro ricoloramento viene semplicemente
 * sostituito. Niente calcoli in piu', niente calcoli in meno: cambia solo QUANDO il cliente vede il colore.
 *
 * I colori sono quelli del riquadro giallo "VAR 6 - COLORI METALLI" del modello (22 set 2026): se Davide li ritocca
 * li', vanno ritoccati anche qui (COLORI). Per sicurezza il fork "impara" anche il colore che vede arrivare dal
 * modello per ogni finitura e, se lo conosce, usa quello.
 *
 * COLORI NITIDI (9 ott 2026, video di Davide dal telefono: "mentre carica la finitura il colore e' piu' saturo, lo
 * vorrei sempre cosi', su PC, tablet e telefono"). Misurato nel visore (anello Philía, colore medio del gioiello):
 * oro giallo arrivato dal calcolo (163,147,110) saturazione 0,34 = slavato; lo STESSO colore #D6A856 riapplicato dal
 * fork (146,113,59) saturazione 0,62 = il colore vero del riquadro VAR 6. Il visore mostra "schiarito" il colore dei
 * materiali che arrivano con la geometria del calcolo; riassegnandolo lo mostra giusto. Quindi a OGNI arrivo delle
 * uscite di metallo e di smalto (useColoriNitidi, montato in EidosVista) si riassegna il loro stesso colore.
 */
import {useShapeDiverStoreSession} from "@AppBuilderLib/entities/session/model/useShapeDiverStoreSession";
import {useShapeDiverStoreViewport} from "@AppBuilderLib/entities/viewport/model/useShapeDiverStoreViewport";
import {GeometryData, ITreeNode} from "@shapediver/viewer.session";
import {useEffect} from "react";

/** colore a schermo di ogni finitura (chiave = parola che compare nel nome della voce del menu) */
const COLORI: Record<string, string> = {
	argento: "#F0F2F7", // 240,242,247
	"oro giallo": "#D6A856", // 214,168,86
	// 228,171,145 (24 ott 2026, Davide: «l'oro rosa deve essere piu' chiaro», scelto il tono A fra tre prove nel visore;
	// prima 217,152,124 dal 13 ott, piu' ramato, e 240,152,148 fino al 13 ott)
	"oro rosa": "#E4AB91",
};
/** colori vecchi che il modello puo' ancora mandare (versioni caricate prima del cambio): si mostrano col colore nuovo.
 *  Cosi' il tono nuovo vale SUBITO per tutti i modelli (CHORA, Týpos, Ónoma) anche prima di ricaricarli su ShapeDiver */
const SOSTITUISCI: Record<string, string> = {
	"#F09894": "#E4AB91", // oro rosa fino al 13 ott 2026
	"#D9987C": "#E4AB91", // oro rosa dal 13 al 24 ott 2026 (riquadro VAR 6 dei modelli caricati fino ad allora)
};
/** colore da mostrare al posto di uno vecchio (maiuscole/minuscole e trasparenza in coda non contano) */
function sostituisci(hex: string): string {
	return SOSTITUISCI[hex.toUpperCase().slice(0, 7)] ?? hex;
}
/** uscite del modello fatte di metallo (le altre: Pietra, IncisioneDisplay non si toccano) */
const USCITE_METALLO = ["Gioiello", "Catena"];

/** (7 ott sera, Davide: "applica anche lo smalto istantaneo") colori a schermo degli smalti = riquadro "VAR 7 - COLORI
 *  SMALTI" del modello; chiave = nome della voce del menu Smalto in minuscolo. Vale solo per il passaggio da un colore a
 *  un altro: accendere o spegnere lo smalto ("Nessuno") aggiunge o toglie materiale e cambia il prezzo -> calcolo normale. */
const COLORI_SMALTO: Record<string, string> = {
	// (27 ott 2026, Davide) in vendita solo nero, rosso, tiffany e bianco (modello v47); blu, verde e azzurro restano qui
	// solo per i modelli caricati prima (nel menu li nasconde EidosSceltaComponent)
	nero: "#141416", // 20,20,22
	rosso: "#BE1923", // 190,25,35
	tiffany: "#81D8D0", // 129,216,208 (Tiffany Blue classico, scelto da Davide il 27 ott)
	bianco: "#F5F5F2", // 245,245,242
	blu: "#14328C", // 20,50,140
	verde: "#147846", // 20,120,70
	azzurro: "#5AB4E6", // 90,180,230
};
const USCITE_SMALTO = ["SmaltoDisplay"];
const vistiSmalto: Record<string, string> = {};

/** colori visti arrivare dal modello, per finitura: se ci sono valgono piu' della tabella */
const visti: Record<string, string> = {};
const diario: string[] = [];

/** "Placcato oro rosa 18 kt" -> "oro rosa"; "Argento 925 rodiato" -> "argento" */
export function chiaveFinitura(nomeVoce: string): string {
	const n = nomeVoce.toLowerCase();
	if (n.includes("rosa")) return "oro rosa";
	if (n.includes("giallo")) return "oro giallo";
	return "argento";
}

function esadecimale(c: unknown): string | null {
	if (typeof c === "string") return c;
	if (Array.isArray(c) && c.length >= 3) {
		const [r, g, b] = c as number[];
		const m = r <= 1 && g <= 1 && b <= 1 ? 255 : 1;
		return "#" + [r, g, b].map((x) => Math.round(x * m).toString(16).padStart(2, "0")).join("").toUpperCase();
	}
	return null;
}

/** colore mostrato ADESSO nel visore dalle uscite date (prima geometria trovata), o null */
function coloreVisto(uscite: string[]): string | null {
	let trovato: string | null = null;
	const {sessions} = useShapeDiverStoreSession.getState();
	for (const s of Object.values(sessions)) {
		for (const nome of uscite) {
			for (const out of s.getOutputByName(nome)) {
				out.node?.traverseData((d) => {
					if (trovato === null && d instanceof GeometryData && d.material) trovato = esadecimale((d.material as unknown as {color: unknown}).color);
				});
			}
		}
	}
	return trovato;
}
export function coloreMetalloVisto(): string | null {
	return coloreVisto(USCITE_METALLO);
}

/** ricolora nel visore tutte le geometrie delle uscite date (senza calcolo); restituisce quante geometrie */
function ricolora(uscite: string[], hex: string): number {
	let n = 0;
	const {sessions} = useShapeDiverStoreSession.getState();
	const materiali = new Set<unknown>();
	for (const s of Object.values(sessions)) {
		for (const nome of uscite) {
			for (const out of s.getOutputByName(nome)) {
				const nodo = out.node;
				if (!nodo) continue;
				nodo.traverseData((d) => {
					if (d instanceof GeometryData && d.material) {
						const m = d.material as unknown as {color: unknown; updateVersion?: () => void};
						if (!materiali.has(m)) {
							m.color = hex;
							m.updateVersion?.();
							materiali.add(m);
						}
						n++;
					}
				});
				nodo.updateVersion();
			}
		}
	}
	for (const v of Object.values(useShapeDiverStoreViewport.getState().viewports)) v.update();
	return n;
}
export function ricoloraMetallo(hex: string): number {
	return ricolora(USCITE_METALLO, hex);
}

/**
 * Da chiamare nell'istante in cui cambia lo Smalto (uiValue, prima del calcolo). Agisce solo se PRIMA e DOPO c'e' un
 * colore (non "Nessuno"): in quel caso lo smalto e' gia' nel visore e basta ricolorarlo.
 */
export function smaltoCambiato(nomeVoceNuova: string, nomeVoceVecchia: string) {
	try {
		const nuova = nomeVoceNuova.trim().toLowerCase();
		const vecchia = nomeVoceVecchia.trim().toLowerCase();
		if (!nuova || !vecchia || nuova === "nessuno" || vecchia === "nessuno") return;
		const corrente = coloreVisto(USCITE_SMALTO);
		if (corrente) vistiSmalto[vecchia] = corrente;
		const hex = vistiSmalto[nuova] ?? COLORI_SMALTO[nuova];
		if (!hex) return;
		const n = ricolora(USCITE_SMALTO, hex);
		diario.push(`${new Date().toISOString().slice(11, 23)} smalto ${vecchia} -> ${nuova} ${hex} (${n} geometrie)`);
		if (diario.length > 30) diario.shift();
	} catch (e) {
		diario.push("errore smalto: " + String((e as Error)?.message ?? e));
	}
}

/**
 * Da chiamare nell'istante in cui cambia la finitura (uiValue, prima del calcolo).
 * nomeVoceNuova = voce appena scelta; nomeVoceVecchia = voce che era in vigore (il colore a schermo e' il suo: lo
 * impariamo prima di cambiarlo).
 */
export function finituraCambiata(nomeVoceNuova: string, nomeVoceVecchia: string) {
	try {
		const vecchia = chiaveFinitura(nomeVoceVecchia);
		const corrente = coloreMetalloVisto();
		if (corrente) visti[vecchia] = corrente;
		const nuova = chiaveFinitura(nomeVoceNuova);
		const scelto = visti[nuova] ?? COLORI[nuova];
		if (!scelto) return;
		const hex = sostituisci(scelto);
		const n = ricoloraMetallo(hex);
		diario.push(`${new Date().toISOString().slice(11, 23)} ${vecchia} -> ${nuova} ${hex} (${n} geometrie)`);
		if (diario.length > 30) diario.shift();
	} catch (e) {
		diario.push("errore: " + String((e as Error)?.message ?? e));
	}
}

/** uscite il cui colore si riassegna a ogni arrivo dal calcolo (vedi COLORI NITIDI in cima al file) */
const USCITE_NITIDE = [...USCITE_METALLO, ...USCITE_SMALTO];
let ravvivati = 0;

/** riassegna a ogni materiale del nodo il suo stesso colore: il visore lo mostra nitido invece che schiarito */
function ravviva(nodo?: ITreeNode): void {
	if (!nodo) return;
	const materiali = new Set<unknown>();
	nodo.traverseData((d) => {
		if (!(d instanceof GeometryData) || !d.material) return;
		const m = d.material as unknown as {color: unknown; updateVersion?: () => void};
		if (materiali.has(m)) return;
		materiali.add(m);
		const letto = esadecimale(m.color);
		if (!letto) return;
		const hex = sostituisci(letto);
		m.color = hex;
		m.updateVersion?.();
	});
	if (!materiali.size) return;
	nodo.updateVersion();
	ravvivati++;
}

/** da montare una volta (EidosVista): a ogni arrivo di Gioiello, Catena e SmaltoDisplay il colore torna nitido */
export function useColoriNitidi(): void {
	const sessions = useShapeDiverStoreSession((s) => s.sessions);
	const chiave = Object.keys(sessions).join("|");
	useEffect(() => {
		const {addOutputUpdateCallback} = useShapeDiverStoreSession.getState();
		const togli: (() => void)[] = [];
		for (const [id, s] of Object.entries(sessions)) {
			for (const nome of USCITE_NITIDE) {
				for (const out of s.getOutputByName(nome)) {
					// la funzione viene chiamata subito con la geometria attuale e poi a ogni calcolo, prima del disegno
					togli.push(addOutputUpdateCallback(id, out.id, (nuovo?: ITreeNode) => ravviva(nuovo)));
				}
			}
		}
		return () => togli.forEach((f) => f());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chiave]);
}

// ganci per le prove dal browser: window.__eidosFinitura.visto() / .ricolora("#D6A856") / .diario() / .imparati()
(window as unknown as {__eidosFinitura: unknown}).__eidosFinitura = {
	visto: coloreMetalloVisto,
	ricolora: ricoloraMetallo,
	diario: () => diario.slice(),
	imparati: () => ({...visti, smalto: {...vistiSmalto}}),
	colori: () => ({...COLORI, smalto: {...COLORI_SMALTO}}),
	vistoSmalto: () => coloreVisto(USCITE_SMALTO),
	ravvivati: () => ravvivati,
};
